import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PublicBrand } from '../components/PublicBrand';
import { ErrorBanner, Spinner } from '../components/common';
import { Upload } from './Upload';
import { ClientForm } from './ClientForm';
import { getUploadContext } from '../api/jobs';
import type { UploadContext } from '../types';

type Tab = 'info' | 'docs';

/**
 * Collect information: one link, two tabs.
 *
 * Replaces two separate client emails and two separate links. The questionnaire used
 * to be sent by hand from the job page after the documents arrived, which meant it was
 * often never sent at all: Owners' Involvement came back empty, Workpapers!H77 read
 * zero, and the director remuneration add-back silently vanished from a workbook that
 * looked finished. Putting both halves behind the link the client already receives on
 * signing removes the step a human has to remember.
 *
 * Both tabs read the same :token from the route, so neither child needs it passed in.
 * The old /upload/:token route redirects here, because links already sitting in
 * clients' inboxes have to keep working.
 */
export function Collect() {
  const { token = '' } = useParams();
  const [ctx, setCtx] = useState<UploadContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('info');
  // What the documents tab currently says is outstanding. The context is a page-load
  // snapshot, so without this the chip still read "2 still to send" while both rows
  // below it were green.
  const [outstanding, setOutstanding] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    getUploadContext(token)
      .then((c) => {
        if (!live) return;
        setCtx(c);
        // Open on the half with work left in it. A client who has answered the
        // questionnaire and come back for the documents should not have to find the
        // right tab first.
        setTab(c.intakeSubmitted ? 'docs' : 'info');
      })
      .catch((err) => live && setLoadError(err instanceof Error ? err.message : String(err)));
    return () => {
      live = false;
    };
  }, [token]);

  if (loadError) {
    return (
      <div className="public-wrap">
        <div className="card public-card">
          <PublicBrand />
          <ErrorBanner message={loadError} />
          <p className="muted">
            This link may have expired. Please contact your WA Business Valuations adviser
            for a new one.
          </p>
        </div>
      </div>
    );
  }
  if (!ctx) return <Spinner label="Loading." />;

  const docsDone =
    outstanding !== null
      ? outstanding === 0
      : ctx.requirements?.length
        ? ctx.requirements.every((r) => r.supplied)
        : (ctx.uploadedCount ?? 0) > 0;
  const stillToSend =
    outstanding ?? ctx.requirements?.filter((r) => !r.supplied).length ?? 0;

  return (
    <div className="public-wrap public-wrap--wide">
      <div className="card public-card" style={{ textAlign: 'center' }}>
        <PublicBrand />
      </div>

      <div className="card">
        <h1>What we need to value {ctx.entityName}</h1>
        <p className="muted">
          Two steps. Start with the general information; the documents unlock once it is
          submitted. Nothing is sent until you press the button at the end of each step,
          and you can close this and come back to the same link.
        </p>

        <div className="collect-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'info'}
            className={`collect-tab${tab === 'info' ? ' active' : ''}${ctx.intakeSubmitted ? ' done' : ''}`}
            onClick={() => setTab('info')}
          >
            <span className="collect-tab-n">{ctx.intakeSubmitted ? '✓' : '1'}</span>
            <span>
              General information
              <small>{ctx.intakeSubmitted ? 'Received, thank you' : 'Directors, owners, customers'}</small>
            </span>
          </button>
          {/*
            Locked until the questionnaire is in. The two halves used to be reachable in
            either order, and the half people skipped was the questionnaire: that is why
            Owners' Involvement arrived empty on the real Blue Jay engagement and the
            director-remuneration add-back silently read zero.
          */}
          <button
            role="tab"
            aria-selected={tab === 'docs'}
            disabled={!ctx.intakeSubmitted}
            title={ctx.intakeSubmitted ? undefined : 'Complete the general information first'}
            className={`collect-tab${tab === 'docs' ? ' active' : ''}${docsDone ? ' done' : ''}${
              ctx.intakeSubmitted ? '' : ' locked'
            }`}
            onClick={() => ctx.intakeSubmitted && setTab('docs')}
          >
            <span className="collect-tab-n">{docsDone ? '✓' : ctx.intakeSubmitted ? '2' : '🔒'}</span>
            <span>
              Upload documents
              <small>
                {!ctx.intakeSubmitted
                  ? 'Complete step 1 first'
                  : docsDone
                    ? 'Received, thank you'
                    : `${stillToSend} still to send`}
              </small>
            </span>
          </button>
        </div>
      </div>

      {/*
        Both children are mounted only when their tab is active. Keeping the inactive
        one alive would leave two forms in the DOM competing for the same token and,
        in the upload case, a live drop target the client cannot see.
      */}
      {/*
        Going back to step 1 after submitting used to render a bare "This form has
        already been submitted" error where the client's answers had been. The data was
        never at risk, the server refuses a second submission, but an error banner is
        the wrong thing to show somebody who just wanted to check what they had sent.
      */}
      {tab === 'info' && ctx.intakeSubmitted ? (
        <div className="card">
          <h2>General information received</h2>
          <p className="muted">
            Thank you, we have your answers and nothing further is needed here. They cannot
            be changed from this link now. If something was wrong or you have remembered
            something, reply to the email that brought you here and we will update it.
          </p>
        </div>
      ) : tab === 'info' || !ctx.intakeSubmitted ? (
        <ClientForm
          embedded
          onDone={() => {
            // Straight on to the documents rather than a thank-you: this is step one
            // of two, and telling somebody they have finished halfway through is how
            // the questionnaire used to arrive without any statements behind it.
            setCtx({ ...ctx, intakeSubmitted: true });
            setTab('docs');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      ) : (
        <Upload embedded onOutstandingChange={setOutstanding} />
      )}
    </div>
  );
}
